/**
 * Collection grid cards — isolated from product-card / card-gallery stack.
 * Cross-color swipe, same-position swatch selection, position-99 hover.
 * Media matching: alt text first, then filename `{name}-{color}-{n|swatch}`.
 */

import { normalizeColorForMedia, prepareMediaAltParseSource } from '@theme/display-text-config';
import { requestIdleCallback } from '@theme/utilities';

const CARD_HOVER_POSITION = 99;
/** @type {Map<string, Promise<object|null>>} */
const productJsonCache = new Map();

/**
 * @param {number | ReturnType<typeof setTimeout>} handle
 */
function cancelScheduledIdle(handle) {
  if (!handle) return;
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(/** @type {number} */ (handle));
  } else {
    clearTimeout(/** @type {ReturnType<typeof setTimeout>} */ (handle));
  }
}

/**
 * Build a Shopify CDN URL for a given width from an existing image_url.
 * @param {string} imageUrl
 * @param {number|string} width
 */
function withImageWidth(imageUrl, width) {
  if (!imageUrl) return '';
  const w = String(width);
  if (/([?&])width=\d+/.test(imageUrl)) {
    return imageUrl.replace(/([?&])width=\d+/, `$1width=${w}`);
  }
  return `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=${w}`;
}

/**
 * Ensure a slide has an <img> before it becomes visible (swipe / swatch / hover).
 * Liquid omits full image markup for other-color / deferred-hover shells.
 * @param {HTMLElement} slide
 * @param {{ priority?: boolean }} [options]
 */
function hydrateSlideImage(slide, options = {}) {
  if (!(slide instanceof HTMLElement)) return;
  const existing = slide.querySelector('img.pz-card__image, img');
  if (existing instanceof HTMLImageElement) {
    if (options.priority) {
      existing.loading = 'eager';
      try {
        existing.fetchPriority = 'high';
      } catch {
        /* older browsers */
      }
    }
    return;
  }

  const imageUrl = slide.dataset.imageUrl || '';
  const mediaSrc = slide.dataset.mediaSrc || '';
  if (!imageUrl && !mediaSrc) return;

  const widths = String(slide.dataset.imageWidths || '240,480,800')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const sizes =
    slide.dataset.imageSizes ||
    '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';
  const baseUrl = imageUrl || mediaSrc;

  const img = document.createElement('img');
  img.className = 'pz-card__image';
  img.alt = slide.dataset.mediaAlt || '';
  img.decoding = 'async';
  img.sizes = sizes;
  if (options.priority) {
    img.loading = 'eager';
    try {
      img.fetchPriority = 'high';
    } catch {
      /* older browsers */
    }
  } else {
    img.loading = 'lazy';
  }

  if (widths.length && baseUrl) {
    img.srcset = widths.map((width) => `${withImageWidth(baseUrl, width)} ${width}w`).join(', ');
  }
  img.src = withImageWidth(baseUrl, widths[widths.length - 1] || 800) || baseUrl;

  slide.appendChild(img);
  slide.removeAttribute('data-needs-hydrate');
}

/**
 * Warm the browser cache for a slide URL without mounting it in the card.
 * @param {HTMLElement | null | undefined} slide
 */
function prefetchSlideImage(slide) {
  if (!(slide instanceof HTMLElement)) return;
  if (slide.querySelector('img.pz-card__image, img')) return;
  const baseUrl = slide.dataset.imageUrl || slide.dataset.mediaSrc || '';
  if (!baseUrl) return;
  const warm = new Image();
  warm.decoding = 'async';
  warm.src = withImageWidth(baseUrl, 800) || baseUrl;
}

/**
 * Parse compact deferred media map: color::url::position::id||...
 * URL may contain "://" — never split naively on "::".
 * @param {string} raw
 */
function parseDeferredMediaMap(raw) {
  /** @type {Record<string, Array<{u: string, p: number, i: string}>>} */
  const map = {};
  if (!raw) return map;

  for (const row of String(raw).split('||')) {
    if (!row) continue;
    const colorEnd = row.indexOf('::');
    if (colorEnd < 0) continue;
    const color = row.slice(0, colorEnd);
    const rest = row.slice(colorEnd + 2);
    // position::id are the last two fields; url is everything before them
    const idSep = rest.lastIndexOf('::');
    if (idSep < 0) continue;
    const id = rest.slice(idSep + 2);
    const beforeId = rest.slice(0, idSep);
    const posSep = beforeId.lastIndexOf('::');
    if (posSep < 0) continue;
    const position = beforeId.slice(posSep + 2);
    const url = beforeId.slice(0, posSep);
    if (!color || !url) continue;
    if (!map[color]) map[color] = [];
    map[color].push({
      u: url,
      p: Number(position) || 1,
      i: id || '',
    });
  }

  return map;
}

/**
 * Read compact media-map JSON from a script node.
 * @param {Element | null | undefined} mapNode
 * @returns {string}
 */
function readMediaMapRaw(mapNode) {
  if (!mapNode?.textContent) return '';
  try {
    return JSON.parse(mapNode.textContent);
  } catch {
    return mapNode.textContent.trim();
  }
}

/** @type {Map<string, Record<string, Array<{u: string, p: number, i: string}>>>} */
const sharedProductMediaMaps = new Map();

/**
 * Resolve deferred media map: local card map → shared once-per-product map.
 * @param {HTMLElement} card
 */
function resolveDeferredMediaMap(card) {
  const localNode =
    card.querySelector('[data-card-media-map]') ||
    card.querySelector('[data-product-media-map]');
  const localRaw = readMediaMapRaw(localNode);
  if (localRaw) {
    const parsed = parseDeferredMediaMap(localRaw);
    const productId = card.dataset.sharedMediaProductId || card.dataset.productId;
    if (productId && card.querySelector('[data-product-media-map]')) {
      sharedProductMediaMaps.set(String(productId), parsed);
    }
    return parsed;
  }

  const sharedId = card.dataset.sharedMediaProductId;
  if (!sharedId) return {};

  const cached = sharedProductMediaMaps.get(String(sharedId));
  if (cached) return cached;

  const sharedNode = document.querySelector(
    `[data-product-media-map][data-product-id="${String(sharedId).replace(/"/g, '')}"]`
  );
  const sharedRaw = readMediaMapRaw(sharedNode);
  if (!sharedRaw) return {};

  const parsed = parseDeferredMediaMap(sharedRaw);
  sharedProductMediaMaps.set(String(sharedId), parsed);
  return parsed;
}

function normalizeColor(value) {
  return normalizeColorForMedia(value);
}

/**
 * Filename fallback: `{prefix}-{color}-{n|swatch}` or `{prefix}-{color}`.
 * Color = segment(s) between dashes; position after color if present, else Shopify order.
 * @param {string} src
 * @param {Set<string>} knownColors
 * @param {number} [shopifyPosition]
 */
function parseFilenameMediaMeta(src, knownColors, shopifyPosition = 999) {
  const empty = { color: '', position: 999, role: 'gallery' };
  const raw = String(src || '').split('?')[0].split('#')[0].split('/').pop() || '';
  const basename = raw.replace(/\.[a-z0-9]+$/i, '').trim().toLowerCase();
  if (!basename) return empty;

  const parts = basename.replace(/_/g, '-').split('-').filter(Boolean);
  if (parts.length < 2) return empty;

  const last = parts[parts.length - 1];
  let role = 'gallery';
  let position = Number.isFinite(shopifyPosition) && shopifyPosition > 0 ? shopifyPosition : 999;
  let colorEnd = parts.length;
  let hasSuffix = false;

  if (last === 'swatch') {
    role = 'swatch';
    position = 0;
    colorEnd = parts.length - 1;
    hasSuffix = true;
  } else {
    const positionNum = Number.parseInt(last, 10);
    if (positionNum > 0 && positionNum <= 999 && String(positionNum) === last) {
      position = positionNum;
      colorEnd = parts.length - 1;
      hasSuffix = true;
      role = positionNum === 1 ? 'primary' : 'gallery';
    }
  }

  if (colorEnd < 1) return empty;

  const tryColor = (rawColor) => {
    const color = normalizeColor(rawColor);
    if (!color) return null;
    if (knownColors.size > 0 && !knownColors.has(color)) return null;
    return color;
  };

  if (hasSuffix) {
    for (let len = 1; len <= colorEnd; len++) {
      const start = colorEnd - len;
      const color = tryColor(parts.slice(start, colorEnd).join('-'));
      if (color) {
        return {
          color,
          position,
          role: role === 'swatch' ? 'swatch' : position === 1 ? 'primary' : 'gallery',
        };
      }
    }
    const fallbackColor = normalizeColor(parts[colorEnd - 1] || '');
    if (!fallbackColor) return empty;
    return {
      color: fallbackColor,
      position,
      role: role === 'swatch' ? 'swatch' : position === 1 ? 'primary' : 'gallery',
    };
  }

  // No position/swatch suffix — trailing segment is color; position = Shopify order.
  for (let len = 1; len < parts.length; len++) {
    const color = tryColor(parts.slice(parts.length - len).join('-'));
    if (color) {
      return {
        color,
        position,
        role: position === 1 ? 'primary' : 'gallery',
      };
    }
  }

  const fallbackColor = normalizeColor(parts[parts.length - 1] || '');
  if (!fallbackColor) return empty;
  return {
    color: fallbackColor,
    position,
    role: position === 1 ? 'primary' : 'gallery',
  };
}

/**
 * Parse media metadata from alt text first, then filename.
 * Format: {color}-{position}, {color}-swatch, or color only (position from Shopify order).
 * Filename: `{product}-{color}-{n|swatch}` (color between dashes).
 */
function parseMediaMeta(src, alt, shopifyPosition, knownColors) {
  const empty = { color: '', position: 999, role: 'gallery' };
  const altRaw = String(alt || '').trim();

  if (altRaw) {
    try {
      const altBase = prepareMediaAltParseSource(altRaw);
      if (altBase) {
        const parts = altBase.split('-').filter(Boolean);

        if (parts.length >= 2) {
          const lastPart = parts[parts.length - 1].trim();

          if (lastPart === 'swatch') {
            const color = normalizeColor(parts.slice(0, -1).join('-'));
            if (color && (knownColors.size === 0 || knownColors.has(color))) {
              return { color, position: 0, role: 'swatch' };
            }
          } else {
            const positionNum = Number.parseInt(lastPart, 10);
            if (positionNum > 0 && positionNum <= 999 && String(positionNum) === lastPart) {
              const color = normalizeColor(parts.slice(0, -1).join('-'));
              if (color && (knownColors.size === 0 || knownColors.has(color))) {
                return {
                  color,
                  position: positionNum,
                  role: positionNum === 1 ? 'primary' : 'gallery',
                };
              }
            }
          }
        }

        const color = normalizeColor(altBase);
        if (color && (knownColors.size === 0 || knownColors.has(color))) {
          const position = Number.isFinite(shopifyPosition) && shopifyPosition > 0 ? shopifyPosition : 999;
          return {
            color,
            position,
            role: position === 1 ? 'primary' : 'gallery',
          };
        }
      }
    } catch {
      /* fall through to filename */
    }
  }

  return parseFilenameMediaMeta(src, knownColors, shopifyPosition);
}

if (!customElements.get('pz-collection-card')) {
  class PzCollectionCard extends HTMLElement {
    #hoverPreviewActive = false;
    #scrollRaf = 0;
    /** @type {number | ReturnType<typeof setTimeout> | 0} */
    #idlePrefetchHandle = 0;
    #idlePrefetchQueued = false;
    /** @type {MediaQueryList | undefined} */
    #navModeQuery;
    /** @type {MediaQueryList | undefined} */
    #hoverModeQuery;
    /** @type {(() => void) | undefined} */
    #onNavModeChange;
    #hadDesktopHover = false;
    #movePending = false;
    /** @type {'none' | 'dense' | 'full'} */
    #eventsMode = 'none';
    /** @type {AbortController | null} */
    #listenerAbort = null;

    #isTouchNav() {
      // Phone only — tablet/iPad use desktop color + arrow nav (≥750).
      return matchMedia('(max-width: 749px)').matches;
    }

    #isDesktopHover() {
      // Real desktop mouse only — never treat phones/tablets as hover-capable.
      return matchMedia('(width >= 750px) and (hover: hover) and (pointer: fine)').matches;
    }

    /** Dense (zoom-out) grid — media-only; skip heavy per-card work. */
    isDenseView() {
      const grid = this.closest('[product-grid-view]');
      return grid?.getAttribute('product-grid-view') === 'zoom-out';
    }

    /**
     * True when gallery navigation is applicable (arrows on desktop hover / swipe on mobile).
     * "Swipe images per color" = 1 still allows cross-color nav when there are
     * multiple colors; a single-color card with limit 1 has nothing to navigate.
     */
    #canSwipeCarousel() {
      if (!this.carouselEnabled || this.isDenseView()) return false;
      if (Array.isArray(this.sequence) && this.sequence.length > 1) return true;
      if (this.#hasDeferredCarouselMedia()) return true;
      // Multi-color cards: nav is applicable even before other-color media materializes.
      return this.swatches.length > 1;
    }

    syncNavMode() {
      const media = this.querySelector('.pz-card__media');
      if (!(media instanceof HTMLElement)) return;

      if (this.isDenseView()) {
        this.touchSwipeMode = false;
        media.classList.remove('card-gallery--touch-swipe');
        media.classList.remove('card-gallery--arrow-nav');
        this.innerSwipeDisabled = true;
        return;
      }

      const canSwipe = this.#canSwipeCarousel();

      // Nested inside a horizontal section carousel (recs / recently viewed / drawers).
      // Touch multi-slide overflow fights the parent slideshow and jumps it to slide 0.
      // Use single-slide + arrows/pointer move instead — only when this card can swipe.
      const inResourceListCarousel = Boolean(
        this.closest('.resource-list__carousel, .search-drawer__trending-carousel')
      );
      if (inResourceListCarousel) {
        this.touchSwipeMode = false;
        media.classList.remove('card-gallery--touch-swipe');
        media.classList.toggle('card-gallery--arrow-nav', canSwipe);
        this.innerSwipeDisabled = !canSwipe;
        return;
      }

      // No second image (and no deferred other-color images) → no swipe chrome.
      if (!canSwipe) {
        this.touchSwipeMode = false;
        media.classList.remove('card-gallery--touch-swipe');
        media.classList.remove('card-gallery--arrow-nav');
        this.innerSwipeDisabled = true;
        return;
      }

      const touchSwipeMode = this.#isTouchNav();
      this.touchSwipeMode = touchSwipeMode;
      media.classList.toggle('card-gallery--touch-swipe', touchSwipeMode);
      media.classList.toggle('card-gallery--arrow-nav', !touchSwipeMode);
      this.innerSwipeDisabled = false;
    }

    applyTouchSequenceLayout(activeIndex = this.currentIndex) {
      // Only hydrate the active slide and immediate neighbors — hydrating every
      // color on multi-swatch cards caused main-thread / memory spikes (and made
      // PDP opens from the card feel frozen after a prior swipe).
      const hydrateFrom = Math.max(0, activeIndex - 1);
      const hydrateTo = Math.min(this.sequence.length - 1, activeIndex + 1);

      this.slides.forEach((slide) => {
        const inSequence = this.sequence.includes(slide);
        if (inSequence) {
          const seqIndex = this.sequence.indexOf(slide);
          if (seqIndex >= hydrateFrom && seqIndex <= hydrateTo) {
            hydrateSlideImage(slide);
          }
          slide.removeAttribute('hidden');
          slide.classList.toggle('is-active', seqIndex === activeIndex);
        } else {
          slide.hidden = true;
          slide.classList.remove('is-active');
        }
      });
    }

    applyDesktopSlideLayout(activeIndex = this.currentIndex) {
      const slide = this.sequence[activeIndex];
      if (!slide) return;

      hydrateSlideImage(slide, { priority: true });

      // Only touch slides that need a visibility change — full forEach was costly
      // once deferred colors had been injected.
      for (const item of this.slides) {
        const on = item === slide;
        if (on) {
          if (item.hidden) item.hidden = false;
          if (!item.classList.contains('is-active')) item.classList.add('is-active');
        } else if (!item.hidden || item.classList.contains('is-active')) {
          item.hidden = true;
          item.classList.remove('is-active');
        }
      }
    }

    #getScrollIndex(scroller) {
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.sequence.forEach((slide, index) => {
        const distance = Math.abs(scroller.scrollLeft - slide.offsetLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    #onSlidesScroll = () => {
      if (!this.touchSwipeMode || !this.carouselEnabled || this.sequence.length < 2) return;

      cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = requestAnimationFrame(() => {
        const scroller = this.querySelector('.pz-card__slides');
        if (!(scroller instanceof HTMLElement)) return;

        const index = this.#getScrollIndex(scroller);

        if (index === this.currentIndex) return;

        const slide = this.sequence[index];
        if (!slide) return;

        this.currentIndex = index;
        this.#hoverPreviewActive = false;
        this.dataset.activeColor = slide.dataset.color || '';
        this.dataset.activePosition = slide.dataset.position || '1';

        hydrateSlideImage(slide);
        const prev = this.sequence[index - 1];
        const next = this.sequence[index + 1];
        if (prev) hydrateSlideImage(prev);
        if (next) hydrateSlideImage(next);

        this.slides.forEach((item) => {
          item.classList.toggle('is-active', item === slide);
        });

        const swatch = this.swatches.find((item) => item.dataset.color === slide.dataset.color);
        if (swatch) this.selectSwatch(swatch);
        else this.updateControls();
      });
    };

    connectedCallback() {
      if (this.dataset.ready === 'true') {
        this.refresh();
        return;
      }

      // refresh() rebuilds slides/swatches/maps — do not duplicate that work here.
      this.limit = Math.max(1, Number(this.dataset.swipeLimit) || 3);
      this.carouselEnabled = this.dataset.carouselEnabled === 'true';
      this.hoverEnabled = this.dataset.hoverEnabled === 'true';
      this.limitedThreshold = Math.max(1, Number(this.dataset.limitedThreshold) || 10);
      this.currentIndex = 0;
      this.dragged = false;
      this.pointerStart = null;
      this.#hoverPreviewActive = false;

      this.refresh();
      this.dataset.ready = 'true';
    }

    /**
     * Drop swatch/media/arrow/hover listeners so a later bindEvents can attach cleanly.
     * Safe after dense exit when overflow-list or morph replaced child nodes.
     * @param {{ clearReady?: boolean }} [options]
     */
    #resetInteractionBindings(options = {}) {
      this.#listenerAbort?.abort();
      this.#listenerAbort = null;
      this.#clearBindFlags();
      this.#eventsMode = 'none';
      if (options.clearReady) delete this.dataset.ready;
      delete this.dataset.eventsBound;
      delete this.dataset.deferredReady;
      delete this.dataset.touchCarouselWarmed;
      this.#hadDesktopHover = false;
      this.removeEventListener('mouseenter', this.#onMouseEnter);
      this.removeEventListener('mouseleave', this.#onMouseLeave);
      this.removeEventListener('pointerenter', this.#prefetchProductPage);
      this.removeEventListener('focusin', this.#prefetchProductPage);
      this.removeEventListener('pointerdown', this.#prefetchProductPage);
    }

    /** Clear morph-sticky bind flags on child nodes. */
    #clearBindFlags() {
      this.querySelectorAll('[data-swatch-bound="true"]').forEach((el) => {
        delete el.dataset.swatchBound;
      });
      const media = this.querySelector('.pz-card__media');
      if (media) delete media.dataset.mediaBound;
      const arrows = this.querySelector('[data-card-arrows]');
      if (arrows) delete arrows.dataset.arrowsBound;
    }

    disconnectedCallback() {
      this.#resetInteractionBindings({ clearReady: true });
      this.#idlePrefetchQueued = false;
      cancelScheduledIdle(this.#idlePrefetchHandle);
      this.#idlePrefetchHandle = 0;
      this.#navModeQuery?.removeEventListener('change', this.#onNavModeChange);
      this.#hoverModeQuery?.removeEventListener('change', this.#onNavModeChange);
      cancelAnimationFrame(this.#scrollRaf);
    }

    /**
     * After dense remorph or dense→regular — always rebind (full cards keep
     * eventsMode "full" through dense, which previously skipped swatch/arrow rebind).
     */
    forceRebindAfterMorph() {
      this.#resetInteractionBindings();
    }

    /**
     * Dense-exit helper — same full reset as remorph.
     */
    prepareDenseExit() {
      this.#resetInteractionBindings();
    }

    /**
     * Active color + immediate swatch neighbors — enough for the interaction frame.
     * @param {string} activeColor
     * @returns {string[]}
     */
    #colorsForInteraction(activeColor) {
      const colors = this.swatches.map((swatch) => swatch.dataset.color).filter(Boolean);
      /** @type {Set<string>} */
      const out = new Set();
      if (activeColor) out.add(activeColor);
      if (!colors.length) return [...out];

      const idx = Math.max(0, colors.indexOf(activeColor));
      if (colors.length > 1) {
        out.add(colors[(idx + 1) % colors.length]);
        out.add(colors[(idx - 1 + colors.length) % colors.length]);
      }
      return [...out];
    }

    /**
     * True when deferred map entries for swatch colors are not yet in the DOM.
     * @returns {boolean}
     */
    #hasUnmaterializedDeferredColors() {
      const map = this.deferredMediaMap;
      if (!map || typeof map !== 'object') return false;

      return this.swatches.some((swatch) => {
        const color = swatch.dataset.color;
        if (!color || !map[color]?.length) return false;

        return map[color].some((entry) => {
          const entryId = String(entry.i || '');
          const position = String(entry.p || 1);
          return !this.slides.some((slide) => {
            if (slide.dataset.color !== color) return false;
            if (entryId && String(slide.dataset.mediaId || '') === entryId) return true;
            return String(slide.dataset.position) === position;
          });
        });
      });
    }

    /**
     * After the active color paints, inject remaining deferred colors off the critical path.
     * Neighbors of `focusColor` are warmed first; `alreadyInjected` colors are skipped.
     * @param {string[]} [alreadyInjected]
     * @param {string} [focusColor]
     */
    #scheduleIdleDeferredPrefetch(alreadyInjected = [], focusColor = '') {
      const map = this.deferredMediaMap;
      if (!map || typeof map !== 'object') {
        this.dataset.deferredReady = 'true';
        return;
      }

      const injected = new Set(alreadyInjected.filter(Boolean));
      const focus = focusColor || alreadyInjected[0] || this.dataset.activeColor || '';
      const warm = this.#colorsForInteraction(focus).filter(
        (color) => color && !injected.has(color) && map[color]?.length
      );
      const warmSet = new Set(warm);
      const rest = this.swatches
        .map((swatch) => swatch.dataset.color)
        .filter(
          (color) => color && !injected.has(color) && !warmSet.has(color) && map[color]?.length
        );

      /** @type {string[]} */
      const remaining = [...warm, ...rest];

      if (!remaining.length) {
        if (!this.#hasUnmaterializedDeferredColors()) {
          this.dataset.deferredReady = 'true';
        }
        return;
      }

      cancelScheduledIdle(this.#idlePrefetchHandle);
      this.#idlePrefetchHandle = 0;
      this.#idlePrefetchQueued = true;

      const run = () => {
        this.#idlePrefetchHandle = 0;
        this.#idlePrefetchQueued = false;
        if (!this.isConnected || this.dataset.deferredReady === 'true') return;

        const color = remaining.shift();
        if (color) {
          const added = this.ensureDeferredSlides(color);
          const primary =
            this.slides.find(
              (slide) => slide.dataset.color === color && Number(slide.dataset.position) === 1
            ) ||
            this.slides.find(
              (slide) =>
                slide.dataset.color === color &&
                Number(slide.dataset.position) > 0 &&
                Number(slide.dataset.position) < CARD_HOVER_POSITION
            );
          prefetchSlideImage(primary);

          if (added) {
            const current = this.sequence?.[this.currentIndex];
            this.#mergeColorIntoSequence(color);
            if (current) {
              const rebuiltIndex = this.sequence.indexOf(current);
              if (rebuiltIndex >= 0) this.currentIndex = rebuiltIndex;
            }
            this.applyDesktopSlideLayout(this.currentIndex);
            this.updateControls();
          }
        }

        if (remaining.length) {
          this.#idlePrefetchQueued = true;
          this.#idlePrefetchHandle = requestIdleCallback(run, { timeout: 500 });
          return;
        }

        if (!this.#hasUnmaterializedDeferredColors()) {
          this.dataset.deferredReady = 'true';
        }
      };

      this.#idlePrefetchHandle = requestIdleCallback(run, { timeout: 400 });
    }

    /**
     * Materialize deferred other-color shells for carousel intent (swipe / arrows).
     * Interaction frame: active color + neighbors only. Remaining colors idle-prefetch.
     * Must NEVER run on tap-to-open PDP — that blocked navigation on multi-color cards.
     * @returns {boolean} whether the sequence was rebuilt
     */
    #materializeDeferredForCarousel() {
      if (!this.carouselEnabled || !this.swatches.length) return false;
      if (this.dataset.deferredReady === 'true') return false;

      const current = this.sequence?.[this.currentIndex];
      const activeColor =
        this.dataset.activeColor ||
        current?.dataset.color ||
        this.swatches[0]?.dataset.color ||
        '';
      const priority = this.#colorsForInteraction(activeColor);

      let added = false;
      for (const color of priority) {
        if (this.ensureDeferredSlides(color)) added = true;
      }

      this.#scheduleIdleDeferredPrefetch(priority, activeColor);

      if (!added) {
        // Map empty or already present — still refresh control visibility.
        this.updateControls();
        return false;
      }

      this.refreshSlideMetadata();
      this.sequence = this.buildSequence();
      if (current) {
        const rebuiltIndex = this.sequence.indexOf(current);
        if (rebuiltIndex >= 0) this.currentIndex = rebuiltIndex;
      }
      this.updateControls();
      return true;
    }

    ensureDeferredSlides(color) {
      const map = this.deferredMediaMap;
      if (!map || typeof map !== 'object') return false;

      const colors = color
        ? [color]
        : this.swatches.map((swatch) => swatch.dataset.color).filter(Boolean);

      const scroller = this.querySelector('[data-card-slides]');
      if (!(scroller instanceof HTMLElement)) return false;

      let added = false;
      const fragment = document.createDocumentFragment();
      const sharedSizes =
        this.querySelector('[data-card-slide][data-image-sizes]')?.dataset.imageSizes ||
        '(min-width: 990px) 25vw, (min-width: 750px) 33vw, 50vw';

      for (const colorKey of colors) {
        if (!colorKey || !map[colorKey]?.length) continue;

        const existingIds = new Set(
          this.slides
            .filter((slide) => slide.dataset.color === colorKey)
            .map((slide) => String(slide.dataset.mediaId || `${slide.dataset.position}`))
        );
        let hasHover = this.slides.some(
          (slide) =>
            slide.dataset.color === colorKey &&
            (Number(slide.dataset.position) === CARD_HOVER_POSITION ||
              slide.hasAttribute('data-hover-slide'))
        );

        for (const entry of map[colorKey]) {
          const entryId = String(entry.i || entry.p || '');
          if (entryId && existingIds.has(entryId)) continue;
          if (!entryId && existingIds.has(String(entry.p || 1))) continue;

          const position = Number(entry.p) > 0 ? Number(entry.p) : 1;
          const isHover = position === CARD_HOVER_POSITION;
          if (isHover && hasHover) continue;

          const figure = document.createElement('figure');
          figure.className = 'pz-card__slide';
          figure.hidden = true;
          figure.dataset.cardSlide = '';
          figure.dataset.mediaId = entry.i || '';
          figure.dataset.color = colorKey;
          figure.dataset.position = String(position);
          if (isHover) {
            figure.dataset.mediaRole = 'hover';
            figure.setAttribute('data-hover-slide', '');
            figure.dataset.mediaAlt = `${colorKey}-99`;
          } else {
            figure.dataset.mediaRole = position === 1 ? 'primary' : 'gallery';
            // Include position in alt so refreshSlideMetadata cannot rewrite it to 999
            figure.dataset.mediaAlt = `${colorKey}-${position}`;
          }
          figure.dataset.deferredSlide = 'true';
          figure.dataset.imageUrl = entry.u || '';
          figure.dataset.mediaSrc = entry.u || '';
          figure.dataset.imageWidths = '240,480,800';
          figure.dataset.imageSizes = sharedSizes;
          figure.dataset.needsHydrate = 'true';
          fragment.appendChild(figure);
          existingIds.add(entryId || String(position));
          if (isHover) hasHover = true;
          added = true;
        }
      }

      if (added) {
        scroller.appendChild(fragment);
        this.slides = Array.from(this.querySelectorAll('[data-card-slide]'));
      }
      return added;
    }

    /**
     * Recommendation / RV cards are section-injected and sometimes omit other-color
     * gallery media + deferred maps. Pull product.js once and materialize slides.
     * @param {string} color
     * @returns {Promise<boolean>}
     */
    async #ensureColorMediaFromProductJson(color) {
      const handle = (this.dataset.productHandle || '').trim();
      if (!handle || !color) return false;

      const hasGallery = this.slides.some(
        (slide) =>
          slide.dataset.color === color &&
          Number(slide.dataset.position) > 0 &&
          Number(slide.dataset.position) < CARD_HOVER_POSITION
      );
      const hasHover = Boolean(this.findHoverSlide(color));
      if (hasGallery && hasHover) return false;

      // Prefer the deferred map when it already has this color.
      const mapped = this.deferredMediaMap?.[color];
      if (Array.isArray(mapped) && mapped.length) {
        const added = this.ensureDeferredSlides(color);
        if (this.findHoverSlide(color) && (hasGallery || this.slides.some((s) => s.dataset.color === color))) {
          return added;
        }
        // Map may lack hover — fall through to product.js for pos 99.
        if (hasGallery && this.findHoverSlide(color)) return added;
      }

      let pending = productJsonCache.get(handle);
      if (!pending) {
        const root =
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const url = `${root}products/${handle}.js`;
        pending = fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null);
        productJsonCache.set(handle, pending);
      }

      const product = await pending;
      const mediaList = Array.isArray(product?.media) ? product.media : [];
      if (!mediaList.length) return false;

      /** @type {Record<string, Array<{u: string, p: number, i: string}>>} */
      const map = { ...(this.deferredMediaMap || {}) };
      const known = this.knownColors instanceof Set ? this.knownColors : new Set();

      for (const media of mediaList) {
        if (media?.media_type && media.media_type !== 'image') continue;
        const src = media?.src || media?.preview_image?.src || '';
        const alt = media?.alt || '';
        const shopifyPosition = Number(media?.position) || 0;
        const meta = parseMediaMeta(src, alt, shopifyPosition, known);
        if (!meta.color || meta.role === 'swatch' || meta.position < 1) continue;
        // Gallery (1–98) + hover (99) only.
        if (meta.position > CARD_HOVER_POSITION) continue;

        if (!map[meta.color]) map[meta.color] = [];
        const id = String(media.id || '');
        if (map[meta.color].some((entry) => String(entry.i) === id && id)) continue;
        if (
          meta.position === CARD_HOVER_POSITION &&
          map[meta.color].some((entry) => Number(entry.p) === CARD_HOVER_POSITION)
        ) {
          continue;
        }
        const width = 1200;
        let url = src;
        if (url && !/[?&]width=\d+/.test(url)) {
          url = `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
        }
        map[meta.color].push({
          u: url,
          p: meta.position,
          i: id,
        });
      }

      this.deferredMediaMap = map;
      return this.ensureDeferredSlides(color);
    }

    refresh() {
      this.slides = Array.from(this.querySelectorAll('[data-card-slide]'));
      this.swatches = Array.from(this.querySelectorAll('[data-card-swatch]'));
      this.knownColors = new Set(
        this.swatches.map((swatch) => swatch.dataset.color).filter(Boolean)
      );
      this.deferredMediaMap = resolveDeferredMediaMap(this);

      // Dense view is image-only — keep one slide, skip carousel/swatch work.
      if (this.isDenseView()) {
        this.bindEvents();
        this.sequence = [];
        this.#hoverPreviewActive = false;
        const active =
          this.slides.find((slide) => slide.classList.contains('is-active')) || this.slides[0];
        this.slides.forEach((slide) => {
          const on = slide === active;
          slide.hidden = !on;
          if (on) {
            hydrateSlideImage(slide);
            slide.removeAttribute('hidden');
          }
          slide.classList.toggle('is-active', on);
        });
        this.classList.remove('is-carousel-active');
        this.classList.add('is-initialized');
        this.syncNavMode();
        return;
      }

      // Leaving dense (or regular remount): upgrade dense-only bindings → full card UX.
      this.bindEvents();

      // Cross-color slides stay deferred until swatch click / first swipe / arrows.
      // Materializing every color on init undoes Liquid deferral and inflates DOM×cards.

      this.refreshSlideMetadata();
      this.sequence = this.buildSequence();

      const wasInitialized = this.classList.contains('is-initialized');
      const preservedIndex =
        wasInitialized &&
        Number.isFinite(this.currentIndex) &&
        this.currentIndex >= 0 &&
        this.sequence[this.currentIndex]
          ? this.currentIndex
          : -1;

      if (preservedIndex >= 0) {
        this.showSlide(preservedIndex, false);
      } else {
        const initialColor = this.dataset.activeColor || '';
        const initialPosition = Number(this.dataset.activePosition || 1);
        const initialSlide =
          this.findColorPosition(initialColor, initialPosition) ||
          this.findLowestPosition(initialColor) ||
          this.sequence.find((slide) => slide.classList.contains('is-active')) ||
          this.sequence[0];

        if (initialSlide) {
          this.showSlide(this.sequence.indexOf(initialSlide), false);
        } else if (this.sequence.length) {
          this.showSlide(0, false);
        } else {
          // Last resort: unhide Liquid is-active or first slide so media is never blank
          const fallback =
            this.slides.find((slide) => slide.classList.contains('is-active')) || this.slides[0];
          if (fallback) {
            hydrateSlideImage(fallback);
            fallback.hidden = false;
            fallback.removeAttribute('hidden');
            fallback.classList.add('is-active');
          }
        }
      }

      this.syncActiveSwatch({ allowFallback: !wasInitialized, chromeOnly: !wasInitialized });
      // Always recompute arrow/swipe chrome after dense exit or remount.
      this.updateControls();
      this.classList.add('is-initialized');
      this.#scheduleTouchCarouselWarmup();
    }

    /**
     * Mobile: inject neighbor-color slides after first paint so the first
     * finger swipe can call move(). Never on pointerdown (PDP tap must stay fast).
     */
    #scheduleTouchCarouselWarmup() {
      if (!this.carouselEnabled || this.isDenseView()) return;
      if (!this.#isTouchNav()) return;
      if (this.dataset.touchCarouselWarmed === 'true') return;
      if (!this.#canSwipeCarousel()) return;

      const warm = () => {
        if (!this.isConnected || !this.#isTouchNav() || this.isDenseView()) return;
        this.dataset.touchCarouselWarmed = 'true';
        this.#materializeDeferredForCarousel();
        // Single visible slide — native overflow inside <a> is what blocked first swipe.
        this.applyDesktopSlideLayout(this.currentIndex);
        this.updateControls();
      };

      requestAnimationFrame(warm);
    }

    /**
     * @param {{ allowFallback?: boolean, chromeOnly?: boolean }} [options]
     */
    syncActiveSwatch(options = {}) {
      if (!this.swatches.length) return;

      const allowFallback = options.allowFallback !== false;
      const activeColor = this.dataset.activeColor || '';
      const preset =
        this.swatches.find((swatch) => swatch.classList.contains('is-active')) ||
        (activeColor ? this.swatches.find((swatch) => swatch.dataset.color === activeColor) : null) ||
        (allowFallback ? this.swatches[0] : null);

      if (!preset) return;

      // First paint: Liquid already rendered the active swatch + badges.
      // Only mount the sliding underline — skip price/badge DOM thrash (load flash).
      const hasReadyIndicator = Boolean(this.querySelector('.pz-card__swatch-indicator.is-ready'));
      if (options.chromeOnly && preset.classList.contains('is-active') && !hasReadyIndicator) {
        this.dataset.activeColor = preset.dataset.color || activeColor;
        this.#syncSwatchUnderline(preset);
        return;
      }

      this.selectSwatch(preset);
    }

    refreshSlideMetadata() {
      this.slides.forEach((slide) => {
        const existingPos = Number(slide.dataset.position);
        const hasTrustedPosition =
          slide.dataset.deferredSlide === 'true' ||
          (Number.isFinite(existingPos) && existingPos > 0 && existingPos < CARD_HOVER_POSITION);

        // Liquid already stamped color/position/role — skip expensive re-parse (INP × cards).
        if (slide.dataset.color && hasTrustedPosition && slide.dataset.mediaRole) {
          if (this.knownColors.size > 0 && !this.knownColors.has(slide.dataset.color)) {
            slide.dataset.color = '';
            slide.dataset.mediaRole = 'orphan';
          }
          return;
        }

        const img = slide.querySelector('img');
        const src =
          slide.dataset.mediaSrc ||
          img?.currentSrc ||
          img?.src ||
          img?.getAttribute('src') ||
          '';
        const alt = slide.dataset.mediaAlt || img?.alt || '';
        const shopifyPosition = Number(slide.dataset.shopifyPosition || 0);
        const meta = parseMediaMeta(src, alt, shopifyPosition, this.knownColors);

        // Keep Liquid / deferred map color+position — reparsing plain color alts
        // was rewriting position to 999 and dropping variant slides from the sequence.
        if (!slide.dataset.color && meta.color) {
          slide.dataset.color = meta.color;
        }
        if (!hasTrustedPosition && meta.position >= 0 && meta.position <= 99) {
          slide.dataset.position = String(meta.position);
        }
        if (!slide.dataset.mediaRole || slide.dataset.mediaRole === 'gallery') {
          const pos = Number(slide.dataset.position);
          slide.dataset.mediaRole =
            meta.role === 'swatch' ? 'swatch' : pos === 1 ? 'primary' : slide.dataset.mediaRole || 'gallery';
        }

        if (this.knownColors.size > 0 && slide.dataset.color && !this.knownColors.has(slide.dataset.color)) {
          slide.dataset.color = '';
          slide.dataset.mediaRole = 'orphan';
        }
      });
    }

    buildSequence() {
      const groups = new Map();
      const colorOrder = this.swatches.map((swatch) => swatch.dataset.color).filter(Boolean);
      const allowedColors = new Set(colorOrder);

      this.slides.forEach((slide) => {
        const position = Number(slide.dataset.position);
        const color = slide.dataset.color || '';
        const role = slide.dataset.mediaRole || 'gallery';

        slide.hidden = true;
        slide.classList.remove('is-active');

        if (!color || position < 1 || position >= CARD_HOVER_POSITION) return;
        if (role === 'swatch' || position === 0) return;
        if (allowedColors.size > 0 && !allowedColors.has(color)) return;

        if (!groups.has(color)) groups.set(color, []);
        groups.get(color).push(slide);
      });

      groups.forEach((slides) => {
        slides.sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
      });

      let sequence =
        colorOrder.length === 0
          ? Array.from(groups.values())
              .flat()
              .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
              .slice(0, this.limit)
          : colorOrder.flatMap((color) => (groups.get(color) || []).slice(0, this.limit));

      // Never leave recommendation/resource cards with every slide hidden.
      // Prefer Liquid is-active, then any non-hover / non-swatch figure.
      if (!sequence.length && this.slides.length) {
        const liquidActive = this.slides.find((slide) => slide.classList.contains('is-active'));
        const anyGallery = this.slides.find((slide) => {
          const role = slide.dataset.mediaRole || 'gallery';
          const position = Number(slide.dataset.position);
          return role !== 'swatch' && position !== 0 && position < CARD_HOVER_POSITION;
        });
        sequence = [liquidActive || anyGallery || this.slides[0]].filter(Boolean);
      }

      return sequence;
    }

    bindEvents() {
      // Dense inject: skip carousel/hover, but still warm PDP on pointerdown.
      if (this.isDenseView()) {
        if (this.#eventsMode === 'full') {
          // Already fully bound — only drop hover while dense CSS hides chrome.
          this.#syncHoverListeners();
          this.classList.add('is-initialized');
          this.classList.remove('is-carousel-active');
          return;
        }
        if (this.#eventsMode === 'dense') return;
        this.#eventsMode = 'dense';
        this.dataset.eventsBound = 'true';
        this.classList.add('is-initialized');
        this.classList.remove('is-carousel-active');
        this.addEventListener('pointerdown', this.#prefetchProductPage, { passive: true });
        return;
      }

      // Regular view — already fully wired.
      if (this.#eventsMode === 'full') {
        this.#syncHoverListeners();
        return;
      }

      // First bind, or upgrade after dense-only / dense-exit reset.
      this.#listenerAbort?.abort();
      this.#listenerAbort = new AbortController();
      const signal = this.#listenerAbort.signal;

      this.#eventsMode = 'full';
      this.dataset.eventsBound = 'true';

      this.syncNavMode();
      if (!this.#navModeQuery) {
        this.#navModeQuery = matchMedia('(max-width: 749px)');
        this.#hoverModeQuery = matchMedia('(width >= 750px) and (hover: hover) and (pointer: fine)');
        this.#onNavModeChange = () => {
          const wasTouch = this.touchSwipeMode;
          const hadHover = this.#hadDesktopHover;
          this.syncNavMode();
          this.#syncHoverListeners();
          if (wasTouch !== this.touchSwipeMode) this.refresh();
          else if (hadHover !== this.#hadDesktopHover && this.#hoverPreviewActive) this.restoreCurrent();
        };
        this.#navModeQuery.addEventListener('change', this.#onNavModeChange);
        this.#hoverModeQuery.addEventListener('change', this.#onNavModeChange);
      }
      this.#syncHoverListeners();

      // Warm PDP only on pointerdown (real intent). Hover prefetch raced quick-add
      // and navigation and made card opens feel frozen across the theme.
      this.addEventListener('pointerdown', this.#prefetchProductPage, { passive: true, signal });

      const arrowsRoot = this.querySelector('[data-card-arrows]');
      if (arrowsRoot) {
        delete arrowsRoot.dataset.arrowsBound;
        arrowsRoot.dataset.arrowsBound = 'true';
        arrowsRoot.addEventListener(
          'click',
          (event) => {
            const control = event.target.closest('.slideshow-control--previous, .slideshow-control--next');
            if (!control || !arrowsRoot.contains(control)) return;

            event.preventDefault();
            event.stopPropagation();
            this.move(control.classList.contains('slideshow-control--previous') ? -1 : 1);
          },
          { capture: true, signal }
        );
      }

      this.swatches.forEach((swatch) => {
        delete swatch.dataset.swatchBound;
        swatch.dataset.swatchBound = 'true';
        swatch.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.onSwatchClick(swatch);
          },
          { signal }
        );
        // Warm deferred media + image cache before the click when possible.
        swatch.addEventListener(
          'pointerenter',
          () => {
            this.#prefetchSwatchColor(swatch.dataset.color || '');
          },
          { passive: true, signal }
        );
        swatch.addEventListener(
          'focus',
          () => {
            this.#prefetchSwatchColor(swatch.dataset.color || '');
          },
          { passive: true, signal }
        );
      });

      const media = this.querySelector('.pz-card__media');
      const scroller = this.querySelector('.pz-card__slides');
      if (!media) return;
      delete media.dataset.mediaBound;
      media.dataset.mediaBound = 'true';

      scroller?.addEventListener('scroll', this.#onSlidesScroll, { passive: true, signal });

      media.addEventListener(
        'pointerdown',
        (event) => {
          if (event.target.closest('button, quick-add-component')) return;

          // Track start for swipe detection in both touch + desktop modes.
          // Do NOT materialize deferred colors here — tap-to-open PDP must stay
          // as fast for 12-color cards as for 1-color cards.
          this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
          this.dragged = false;
          this.swipeAxis = null;
        },
        { signal }
      );

      media.addEventListener(
        'pointermove',
        (event) => {
          if (
            !this.pointerStart ||
            event.pointerId !== this.pointerStart.id ||
            !this.carouselEnabled ||
            this.innerSwipeDisabled
          ) {
            return;
          }

          const dx = event.clientX - this.pointerStart.x;
          const dy = event.clientY - this.pointerStart.y;

          if (!this.swipeAxis) {
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
            this.swipeAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

            // Confirmed horizontal carousel intent — materialize other colors once.
            if (this.swipeAxis === 'x') {
              if (this.#materializeDeferredForCarousel()) {
                this.applyDesktopSlideLayout(this.currentIndex);
              }
              try {
                media.setPointerCapture(event.pointerId);
              } catch {
                /* capture optional */
              }
            }
          }

          // JS swipe on phone + desktop — native overflow-x inside <a> never
          // starts until arrows/swatch inject extra slides.
          if (this.swipeAxis === 'x') {
            event.preventDefault();
            this.dragged = true;
          }
        },
        { passive: false, signal }
      );

      const endPointer = (event) => {
        if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;

        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        const axis = this.swipeAxis;
        this.pointerStart = null;
        this.swipeAxis = null;

        if (axis !== 'x' || Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) return;

        this.dragged = true;
        this.move(dx < 0 ? 1 : -1);
      };

      media.addEventListener('pointerup', endPointer, { signal });
      media.addEventListener('pointercancel', endPointer, { signal });

      // iOS: pointermove preventDefault is unreliable on an <a>; block native
      // pan once the gesture is confirmed horizontal so move() can run.
      media.addEventListener(
        'touchmove',
        (event) => {
          if (this.swipeAxis === 'x' && event.cancelable) event.preventDefault();
        },
        { passive: false, signal }
      );

      media.addEventListener(
        'click',
        (event) => {
          if (!this.dragged) return;
          event.preventDefault();
          this.dragged = false;
        },
        { signal }
      );
    }

    #onMouseEnter = () => this.showHover();

    #onMouseLeave = () => this.restoreCurrent();

    /** Prefetch disabled: racing the click navigation on heavy multi-color PDPs
     * caused long freezes / “refresh” feel. Browser HTTP cache still helps repeat visits. */
    #prefetchProductPage = () => {};

    #syncHoverListeners() {
      if (this.isDenseView()) {
        if (this.#hadDesktopHover) {
          this.removeEventListener('mouseenter', this.#onMouseEnter);
          this.removeEventListener('mouseleave', this.#onMouseLeave);
          this.restoreCurrent();
        }
        this.#hadDesktopHover = false;
        return;
      }

      const enableHover = this.#isDesktopHover();

      if (enableHover && !this.#hadDesktopHover) {
        this.addEventListener('mouseenter', this.#onMouseEnter);
        this.addEventListener('mouseleave', this.#onMouseLeave);
      } else if (!enableHover && this.#hadDesktopHover) {
        this.removeEventListener('mouseenter', this.#onMouseEnter);
        this.removeEventListener('mouseleave', this.#onMouseLeave);
        this.restoreCurrent();
      }

      this.#hadDesktopHover = enableHover;
    }

    canShowHoverPreview() {
      if (this.isDenseView()) return false;
      if (!this.hoverEnabled || !this.#isDesktopHover()) return false;
      return true;
    }

    findHoverSlide(color) {
      if (!color) return null;

      return (
        this.slides.find(
          (slide) =>
            slide.dataset.color === color &&
            (Number(slide.dataset.position) === CARD_HOVER_POSITION || slide.hasAttribute('data-hover-slide'))
        ) || null
      );
    }

    /**
     * Warm deferred shells + primary image for a swatch color ahead of click.
     * @param {string} color
     */
    #prefetchSwatchColor(color) {
      if (!color || this.isDenseView()) return;

      this.ensureDeferredSlides(color);

      const primary =
        this.slides.find(
          (slide) =>
            slide.dataset.color === color &&
            Number(slide.dataset.position) === 1
        ) ||
        this.slides
          .filter(
            (slide) =>
              slide.dataset.color === color &&
              Number(slide.dataset.position) > 0 &&
              Number(slide.dataset.position) < CARD_HOVER_POSITION
          )
          .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))[0];

      prefetchSlideImage(primary);
      prefetchSlideImage(this.findHoverSlide(color));
    }

    /**
     * Merge newly injected deferred slides for one color into the swipe sequence
     * without re-parsing metadata for every slide on the card.
     * @param {string} color
     */
    #mergeColorIntoSequence(color) {
      if (!color) return;

      const limit = this.limit;
      const colorSlides = this.slides
        .filter((slide) => {
          const position = Number(slide.dataset.position);
          const role = slide.dataset.mediaRole || 'gallery';
          return (
            slide.dataset.color === color &&
            position > 0 &&
            position < CARD_HOVER_POSITION &&
            role !== 'swatch'
          );
        })
        .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
        .slice(0, limit);

      if (!colorSlides.length) return;

      const colorOrder = this.swatches.map((swatch) => swatch.dataset.color).filter(Boolean);
      if (!colorOrder.length) {
        this.sequence = this.buildSequence();
        return;
      }

      /** @type {HTMLElement[]} */
      const next = [];
      for (const key of colorOrder) {
        if (key === color) {
          next.push(...colorSlides);
          continue;
        }
        const existing = (this.sequence || []).filter((slide) => slide.dataset.color === key);
        if (existing.length) {
          next.push(...existing.slice(0, limit));
        }
      }

      this.sequence = next.length ? next : this.buildSequence();
    }

    onSwatchClick(swatch) {
      const color = swatch.dataset.color || '';
      if (!color) return;

      // Already showing this color (and not a hover-preview restore) — chrome only.
      if (
        color === this.dataset.activeColor &&
        !this.#hoverPreviewActive &&
        swatch.classList.contains('is-active')
      ) {
        return;
      }

      const currentPosition = Number(
        this.dataset.activePosition || this.sequence?.[this.currentIndex]?.dataset.position || 1
      );

      // Inject only this color on the interaction frame.
      const rebuilt = this.ensureDeferredSlides(color);
      if (rebuilt) {
        // Deferred shells already carry trusted color/position — skip full metadata pass.
        this.#mergeColorIntoSequence(color);
      }

      let target =
        this.findColorPosition(color, currentPosition) || this.findLowestPosition(color);

      if (!target) {
        // Sequence may still lack the color (first inject edge) — resolve from slides.
        const fromSlides = this.slides
          .filter(
            (slide) =>
              slide.dataset.color === color &&
              Number(slide.dataset.position) > 0 &&
              Number(slide.dataset.position) < CARD_HOVER_POSITION
          )
          .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position));
        target =
          fromSlides.find((slide) => Number(slide.dataset.position) === currentPosition) ||
          fromSlides[0] ||
          null;
        if (target && rebuilt) this.#mergeColorIntoSequence(color);
      }

      if (target) {
        // Paint media in this turn, then defer price / ATC / badge work so the
        // browser can commit the image swap sooner (better INP + perceived speed).
        let index = this.sequence.indexOf(target);
        if (index < 0) {
          this.#mergeColorIntoSequence(color);
          index = this.sequence.indexOf(target);
        }

        this.#hoverPreviewActive = false;
        if (index >= 0) {
          hydrateSlideImage(target, { priority: true });
          this.applyDesktopSlideLayout(index);
          this.currentIndex = index;
        } else {
          hydrateSlideImage(target, { priority: true });
          const prev = this.slides.find((slide) => slide.classList.contains('is-active'));
          if (prev && prev !== target) {
            prev.hidden = true;
            prev.classList.remove('is-active');
          }
          target.hidden = false;
          target.classList.add('is-active');
        }
        this.dataset.activeColor = color;
        this.dataset.activePosition = target.dataset.position || '1';

        // Immediate swatch chrome (active + sliding underline) — before media settles.
        this.swatches.forEach((item) => {
          const active = item === swatch;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.#syncSwatchUnderline(swatch);

        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.selectSwatch(swatch, { skipUnderline: true });
          this.updateControls();
        });

        this.#scheduleIdleDeferredPrefetch([color], color);
        return;
      }

      // Instant chrome while media resolves for rec cards without a map.
      this.selectSwatch(swatch);

      this.#ensureColorMediaFromProductJson(color).then((loaded) => {
        if (!loaded) return;
        this.dataset.deferredReady = 'false';
        this.#mergeColorIntoSequence(color);
        if (!this.sequence.some((slide) => slide.dataset.color === color)) {
          this.refreshSlideMetadata();
          this.sequence = this.buildSequence();
        }
        this.updateControls();
        const resolved =
          this.findColorPosition(color, currentPosition) || this.findLowestPosition(color);
        if (resolved) this.showSlide(this.sequence.indexOf(resolved), false);
        this.#scheduleIdleDeferredPrefetch([color], color);
      });
    }

    findColorPosition(color, position) {
      const targetPosition = Number(position);
      if (!color || !Number.isFinite(targetPosition)) return null;

      return (
        this.sequence.find(
          (slide) => slide.dataset.color === color && Number(slide.dataset.position) === targetPosition
        ) || null
      );
    }

    findLowestPosition(color) {
      const slides = this.sequence.filter((slide) => slide.dataset.color === color);
      if (!slides.length) return null;

      return slides.reduce((lowest, slide) =>
        Number(slide.dataset.position) < Number(lowest.dataset.position) ? slide : lowest
      );
    }

    move(delta) {
      if (!this.carouselEnabled) return;

      // Arrow / drag carousel intent — safe to materialize (not a PDP navigation tap).
      if (this.#materializeDeferredForCarousel()) {
        this.applyDesktopSlideLayout(this.currentIndex);
      }

      if (this.sequence.length < 2) {
        // Rec / AJAX cards may still lack other-color media — resolve then retry once.
        if (this.swatches.length > 1 && !this.#movePending) {
          const colors = this.swatches.map((s) => s.dataset.color).filter(Boolean);
          const active = this.dataset.activeColor || colors[0] || '';
          const idx = Math.max(0, colors.indexOf(active));
          const nextColor =
            colors[(idx + (delta > 0 ? 1 : colors.length - 1)) % colors.length] || '';
          if (!nextColor) return;
          this.#movePending = true;
          this.#ensureColorMediaFromProductJson(nextColor)
            .then((loaded) => {
              this.#movePending = false;
              if (!loaded) return;
              this.dataset.deferredReady = 'false';
              this.#materializeDeferredForCarousel();
              if (this.sequence.length >= 2) this.move(delta);
            })
            .catch(() => {
              this.#movePending = false;
            });
        }
        return;
      }
      const next = (this.currentIndex + delta + this.sequence.length) % this.sequence.length;
      this.showSlide(next, true);
    }

    showSlide(index, syncSwatch) {
      if (index < 0 || index >= this.sequence.length) return;

      this.#hoverPreviewActive = false;
      const slide = this.sequence[index];
      hydrateSlideImage(slide, { priority: true });

      // Always one visible slide. Native overflow-x inside the card <a> does not
      // receive the first touch pan; arrows/swatch already used this JS path.
      this.applyDesktopSlideLayout(index);

      this.currentIndex = index;
      this.dataset.activeColor = slide.dataset.color || '';
      this.dataset.activePosition = slide.dataset.position || '1';

      if (syncSwatch) {
        const swatch = this.swatches.find((item) => item.dataset.color === slide.dataset.color);
        if (swatch) this.selectSwatch(swatch);
      }

      this.updateControls();
    }

    /**
     * @param {HTMLElement} selected
     * @param {{ skipUnderline?: boolean, force?: boolean }} [options]
     */
    selectSwatch(selected, options = {}) {
      const color = selected.dataset.color || '';
      const variantId = selected.dataset.variantId || '';
      const variantInput = this.querySelector('quick-add-component input[name="id"]');
      const inputMatches = !variantId || !(variantInput instanceof HTMLInputElement) || variantInput.value === variantId;
      const alreadyApplied =
        !options.force &&
        selected.classList.contains('is-active') &&
        this.dataset.activeColor === color &&
        inputMatches;

      if (alreadyApplied) {
        if (!options.skipUnderline) this.#syncSwatchUnderline(selected);
        return;
      }

      this.swatches.forEach((swatch) => {
        const active = swatch === selected;
        swatch.classList.toggle('is-active', active);
        swatch.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.dataset.activeColor = color;
      const href = `${this.dataset.productUrl}?variant=${variantId}`;
      this.querySelectorAll('[data-card-link]').forEach((link) => link.setAttribute('href', href));

      const price = this.querySelector('[data-card-price]');
      const compare = this.querySelector('[data-card-compare]');
      if (price) price.textContent = selected.dataset.price || '';
      if (compare) {
        const onSale = Number(selected.dataset.compareValue) > Number(selected.dataset.priceValue);
        compare.hidden = !onSale;
        compare.textContent = onSale ? selected.dataset.compare || '' : '';
      }

      const available = selected.dataset.available === 'true';
      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = variantId;
        variantInput.disabled = !available;
      }

      // Cards: OOS → disabled Sold out (Notify Me / Pre-order are PDP-only).
      const quickAdd = this.querySelector('quick-add-component');
      if (quickAdd instanceof HTMLElement) {
        quickAdd.classList.toggle('quick-add--sold-out', !available);
        const soldOutLabel = quickAdd.dataset.soldOutLabel || 'Sold out';
        const addLabel = quickAdd.dataset.addLabel || 'Add';
        // Never disable the choose (drawer) control — shoppers can still open sizes.
        // Only toggle sold-out styling / disable on direct-add submit buttons.
        const chooseButton = quickAdd.querySelector('.quick-add__button--choose');
        const atcButton = quickAdd.querySelector(
          'button[ref="addToCartButton"], .quick-add__button--add'
        );
        if (chooseButton instanceof HTMLButtonElement) {
          chooseButton.disabled = false;
          chooseButton.removeAttribute('aria-disabled');
          chooseButton.classList.toggle('quick-add__button--sold-out', !available);
        }
        if (atcButton instanceof HTMLButtonElement) {
          atcButton.disabled = !available;
          atcButton.setAttribute('aria-disabled', available ? 'false' : 'true');
          atcButton.classList.toggle('quick-add__button--sold-out', !available);
          const nextLabel = available ? addLabel : soldOutLabel;
          atcButton
            .querySelectorAll('.add-to-cart-text__content, .quick-add__label--compact, .quick-add__label--full')
            .forEach((el) => {
              el.textContent = nextLabel;
            });
        }
        // Clear QA HTML cache off the interaction path (INP).
        if (typeof quickAdd.clearCache === 'function') {
          requestIdleCallback(() => {
            try {
              quickAdd.clearCache();
            } catch (e) {
              /* ignore */
            }
          });
        }
      }

      this.updateBadges(selected);
      if (!options.skipUnderline) this.#syncSwatchUnderline(selected);
    }

    /**
     * Slide the shared active underline under the selected swatch.
     * Y is CSS `bottom` — JS only updates X + width (avoids vertical jump).
     * @param {HTMLElement} selected
     */
    #syncSwatchUnderline(selected) {
      const row = selected?.closest?.('.pz-card__swatches');
      if (!(row instanceof HTMLElement) || !(selected instanceof HTMLElement)) return;

      const face = selected.querySelector('.swatch') || selected;
      const left = selected.offsetLeft + (face === selected ? 0 : face.offsetLeft);
      const width = Math.max(0, face.offsetWidth || selected.offsetWidth);

      let indicator = row.querySelector('.pz-card__swatch-indicator');
      const firstPaint = !(indicator instanceof HTMLElement) || !indicator.classList.contains('is-ready');

      if (!(indicator instanceof HTMLElement)) {
        indicator = document.createElement('span');
        indicator.className = 'pz-card__swatch-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        // Position BEFORE insert so the first paint never shows a 0,0 line
        // or a gap where the static ::after was already hidden.
        indicator.style.transition = 'none';
        indicator.style.width = `${width}px`;
        indicator.style.transform = `translate3d(${Math.max(0, left)}px, 0, 0)`;
        indicator.classList.add('is-ready');
        row.appendChild(indicator);
        // Re-enable transitions after this frame (user taps only).
        requestAnimationFrame(() => {
          if (indicator.isConnected) indicator.style.transition = '';
        });
      } else if (firstPaint) {
        indicator.style.transition = 'none';
        indicator.style.width = `${width}px`;
        indicator.style.transform = `translate3d(${Math.max(0, left)}px, 0, 0)`;
        indicator.classList.add('is-ready');
        requestAnimationFrame(() => {
          if (indicator.isConnected) indicator.style.transition = '';
        });
      } else {
        indicator.style.width = `${width}px`;
        indicator.style.transform = `translate3d(${Math.max(0, left)}px, 0, 0)`;
      }

      // Nudge horizontal scroller only when clipped — instant to avoid fighting the line tween.
      const rowLeft = row.scrollLeft;
      const rowRight = rowLeft + row.clientWidth;
      const selLeft = selected.offsetLeft;
      const selRight = selLeft + selected.offsetWidth;
      if (selLeft < rowLeft || selRight > rowRight) {
        const nextLeft = Math.max(0, selLeft - (row.clientWidth - selected.offsetWidth) / 2);
        row.scrollTo({ left: nextLeft, behavior: 'auto' });
        const leftAfter = selected.offsetLeft + (face === selected ? 0 : face.offsetLeft);
        indicator.style.transform = `translate3d(${Math.max(0, leftAfter)}px, 0, 0)`;
      }
    }

    updateBadges(selected) {
      const available = selected.dataset.available === 'true';
      const price = Number(selected.dataset.priceValue || 0);
      const compare = Number(selected.dataset.compareValue || 0);
      const inventory = Number(selected.dataset.inventory || 0);
      // Exact % from amounts: round((compare - price) / compare * 100)
      const discountPct =
        compare > price && compare > 0
          ? Math.round(((compare - price) / compare) * 100)
          : 0;
      const states = [
        ['.product-badges__badge--sold-out', !available],
        ['.product-badges__badge--sale', available && compare > price],
        ['.product-badges__badge--discount-percentage', available && discountPct > 0],
        [
          '.product-badges__badge--limited-quantity',
          available && inventory > 0 && inventory <= this.limitedThreshold,
        ],
      ];

      states.forEach(([selector, visible]) => {
        this.querySelectorAll(selector).forEach((badge) => {
          const isVisible = badge.classList.contains('badge-visible');
          if (isVisible !== visible) {
            badge.classList.toggle('badge-visible', visible);
            badge.style.display = visible ? 'flex' : 'none';
          } else if (!visible && badge.style.display !== 'none') {
            // Keep SSR inline display in sync without clearing it (clearing caused flashes).
            badge.style.display = 'none';
          }
          if (selector === '.product-badges__badge--discount-percentage' && visible) {
            const template =
              badge.dataset.percentTemplate ||
              badge.getAttribute('data-percent-template') ||
              '';
            if (template) {
              badge.textContent = template.replace(/\{\{\s*percentage\s*\}\}/gi, String(discountPct));
            } else {
              badge.textContent = badge.textContent.replace(/\d+/, String(discountPct));
            }
          }
        });
      });
    }

    showHover() {
      if (!this.canShowHoverPreview()) return;

      const color = this.dataset.activeColor || '';
      if (!color) return;

      // Other-color hover shells are deferred — inject before looking up.
      if (!this.findHoverSlide(color)) {
        this.ensureDeferredSlides(color);
      }

      const hover = this.findHoverSlide(color);
      if (!hover) return;

      hydrateSlideImage(hover, { priority: true });
      this.#hoverPreviewActive = true;
      this.slides.forEach((slide) => {
        const show = slide === hover;
        slide.hidden = !show;
        if (show) {
          slide.removeAttribute('hidden');
        }
        slide.classList.toggle('is-active', show);
      });
    }

    restoreCurrent() {
      if (!this.#hoverPreviewActive) return;
      this.#hoverPreviewActive = false;
      if (this.sequence.length) this.showSlide(this.currentIndex, false);
    }

    /**
     * True when deferred other-color gallery slides exist but are not in the DOM yet.
     * Used so nav / swipe can activate before the first swipe materializes them.
     */
    #hasDeferredCarouselMedia() {
      if (this.dataset.deferredReady === 'true') return false;
      const map = this.deferredMediaMap;
      if (!map || typeof map !== 'object') return false;
      return Object.keys(map).some((key) => {
        const entries = map[key];
        return Array.isArray(entries) && entries.length > 0;
      });
    }

    updateControls() {
      // Multi-color + swipe limit 1: sequence starts at 1, deferred other colors
      // still count so arrows/swipe turn on. Single-color + limit 1: stay off.
      const showControls = this.#canSwipeCarousel();

      this.classList.toggle('is-carousel-active', showControls);
      this.classList.add('is-initialized');
      this.syncNavMode();
    }
  }

  customElements.define('pz-collection-card', PzCollectionCard);

  /**
   * Remount only cards under an explicit root.
   * Never default to `document` — unscoped mounts freeze PLP when cart/search update.
   * Callers must pass `detail.root` (Element). Theme Editor section:load uses event.target.
   * @param {Event & { detail?: { root?: Element, dense?: boolean, reason?: string } }} event
   */
  const refreshAllPzCards = (event) => {
    // Dense inject already initializes each card via connectedCallback.
    // Re-refreshing hundreds of cards freezes the main thread.
    if (event?.detail?.dense) return;

    let root = null;
    if (event?.detail?.root instanceof Element) {
      root = event.detail.root;
    } else if (event?.type === 'shopify:section:load' && event.target instanceof Element) {
      root = event.target;
    }

    if (!(root instanceof Element)) return;

    const reason = event?.detail?.reason || '';
    const afterMorph = reason === 'dense-restore';
    const afterDenseExit = reason === 'dense-exit';

    requestAnimationFrame(() => {
      root.querySelectorAll('pz-collection-card').forEach((card) => {
        // Always fully reset after dense — cards that stayed in "full" mode through
        // dense skip rebind otherwise, and overflow-list may have replaced swatches.
        if (
          (afterMorph || afterDenseExit) &&
          typeof card.prepareDenseExit === 'function'
        ) {
          card.prepareDenseExit();
        }
        if (typeof card.refresh === 'function') card.refresh();
      });
    });
  };

  document.addEventListener('shopify:section:load', refreshAllPzCards);
  document.addEventListener('pz-collection-cards:mount', refreshAllPzCards);
  // First paint: each card initializes in connectedCallback — no mass DOMContentLoaded refresh
}
